/// <reference types="google.maps" />
import { AUTOCOMPLETE_DEBOUNCE_MS, autocompleteEnabled } from "@/lib/browser-paid-apis";
import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/google-maps-loader";
import {
  classifyPlacesError,
  failureFor,
  mapPredictions,
  shouldQueryAutocomplete,
  toSelectedPlace,
  type PlaceSuggestion,
  type PlacesFailure,
} from "@/lib/places-autocomplete-policy";

export type PlaceResult = {
  city: string;
  country: string;
  countryCode: string;
  lat: number;
  lng: number;
  placeId?: string;
};

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSelect: (p: PlaceResult) => void;
  placeholder?: string;
  language?: string;
  className?: string;
};

type Suggestion = PlaceSuggestion;

export function PlacesAutocompleteInput({
  value,
  onChange,
  onSelect,
  placeholder,
  language = "en",
  className,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesLibRef = useRef<google.maps.PlacesLibrary | null>(null);
  const debounceRef = useRef<number | null>(null);
  const reqIdRef = useRef(0);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  // Degraded mode: autocomplete unavailable, manual typing still works.
  const [failure, setFailure] = useState<PlacesFailure | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(language)
      .then(async () => {
        const lib = (await google.maps.importLibrary("places")) as google.maps.PlacesLibrary;
        if (cancelled) return;
        placesLibRef.current = lib;
        sessionRef.current = new lib.AutocompleteSessionToken();
        setFailure(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setFailure(classifyPlacesError(error));
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = (input: string) => {
    const lib = placesLibRef.current;
    // Feature flag de custo do browser (H.4): sem autocomplete, o campo
    // continua a funcionar como texto livre.
    if (!autocompleteEnabled() || !shouldQueryAutocomplete(input)) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    if (!lib) {
      setSuggestions([]);
      setFailure((current) => current ?? failureFor("script_not_loaded"));
      return;
    }
    const myReq = ++reqIdRef.current;
    lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      sessionToken: sessionRef.current ?? undefined,
      includedPrimaryTypes: ["locality", "administrative_area_level_1", "country", "sublocality"],
      language,
    })
      .then(({ suggestions: sugs }) => {
        if (myReq !== reqIdRef.current) return;
        const mapped = mapPredictions(sugs as any);
        setSuggestions(mapped);
        setOpen(mapped.length > 0);
        setActiveIdx(-1);
        setFailure(mapped.length === 0 ? failureFor("empty") : null);
      })
      .catch((error) => {
        if (myReq !== reqIdRef.current) return;
        setSuggestions([]);
        setOpen(false);
        setFailure(classifyPlacesError(error));
      });
  };

  const handleChange = (v: string) => {
    onChange(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(
      () => fetchSuggestions(v),
      AUTOCOMPLETE_DEBOUNCE_MS,
    );
  };

  const selectSuggestion = async (s: Suggestion) => {
    const lib = placesLibRef.current;
    if (!lib) {
      setFailure(failureFor("script_not_loaded"));
      return;
    }
    try {
      const place = new lib.Place({ id: s.placeId });
      await place.fetchFields({ fields: ["location", "addressComponents", "displayName"] });
      const loc = place.location;
      const resolved = toSelectedPlace({
        placeId: s.placeId,
        displayName: place.displayName ?? null,
        fallbackName: s.primary,
        lat: loc?.lat() ?? null,
        lng: loc?.lng() ?? null,
        addressComponents: (place.addressComponents ?? []) as any,
      });
      if (!resolved.ok) {
        setFailure(failureFor("no_coordinates"));
        return;
      }
      onChange(resolved.place.city || resolved.place.name);
      onSelect({
        city: resolved.place.city || resolved.place.name,
        country: resolved.place.country,
        countryCode: resolved.place.countryCode,
        lat: resolved.place.lat,
        lng: resolved.place.lng,
        placeId: resolved.place.placeId,
      });
      setOpen(false);
      setSuggestions([]);
      setFailure(null);
      // new session after a selection
      sessionRef.current = new lib.AutocompleteSessionToken();
    } catch (error) {
      setFailure(classifyPlacesError(error));
    }
  };


  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative flex-1">
      <input
        ref={inputRef}
        data-places-autocomplete=""

        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        aria-label="Search location"
        aria-invalid={failure ? true : undefined}
        aria-describedby={failure ? "places-autocomplete-status" : undefined}
        autoComplete="off"
      />
      {failure && (
        <p
          id="places-autocomplete-status"
          role="status"
          aria-live="polite"
          className="mt-1.5 text-xs leading-snug text-muted-foreground"
        >
          {failure.message}
        </p>
      )}

      {open && suggestions.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-lg"
          role="listbox"
        >
          {suggestions.map((s, idx) => (
            <li key={s.placeId}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(s);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left text-sm transition ${
                  activeIdx === idx ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                }`}
              >
                <span className="font-medium">{s.primary}</span>
                {s.secondary && (
                  <span className="text-xs text-muted-foreground">{s.secondary}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
